/* eslint-disable  react/no-multi-comp */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { Modal, Alert, Flex, Row, Col } from '@app/ui';

import { Link } from 'react-router-dom';

import { Client, Collection } from '@app/types/api';

import getTruthyKeys from '@app/utils/getTruthyKeys';
import { getCollectionsTableColumns } from '../../hooks/getCollectionsTableColumns';

import actions from '@app/actions/collections';

import ErrorBoundary from '@app/ErrorBoundary';
import AutomatedParamDrawer from '@app/components/automated-param-modal/AutomatedParamDrawer';
import { Notification } from '@app/components/notifications/Notifications';

import connect from '@app/utils/connect';
import {
    AUTOMATED_COLLECTIONS,
    EDGE_RIGHTSHOLDER_ATTRIBUTION,
} from '@app/constants/config';

import { ClientHeader } from '@app/components/common/ClientHeader';
import CrashedPage from '@app/components/errors/CrashedPage';
import { CollectionsTable } from '../CollectionsTable/CollectionsTable';

import '../../collections.scss';

type CollectionsProps = {
    getCollections: (clientId: number) => void;
    archiveCollections: (clientId: number, collectionIds: number[]) => void;
    createCollection: (clientId: number, values: any) => void;
    updateCollection: (clientId: number, values: any) => void;
    sendNotifications: (
        text: string | React.ReactNode,
        options?: Notification['options'],
        shouldAutoClose?: boolean,
        hasActionButton?: boolean,
    ) => void;
    collections: any;
    client: Client;
    getFilters: () => void;
};

const Collections = ({
    collections,
    getCollections,
    client,
    archiveCollections,
    createCollection,
    updateCollection,
    sendNotifications,
    getFilters,
}: CollectionsProps) => {
    const { t } = useTranslation(['tools', 'common', 'measurement']);
    const [isCreateOpen, setCreateOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [selected, setSelected] = useState({});

    const { items, isLoading } = collections.list;
    const hasAutomatedCollections = client.permissions.includes(
        AUTOMATED_COLLECTIONS,
    );
    const selectedArr = getTruthyKeys(selected);
    const hasSelection = !!selectedArr.length;
    const _items: (Collection & { methodology: 'team' | 'league' })[] = useMemo(
        () =>
            (Object.values(items) as Collection[]).map(c => ({
                ...c,
                methodology: c.isUsingRha === true ? 'league' : 'team',
            })),
        [items],
    );

    const isAllSelected =
        _items.length > 0 && _items.every((item: any) => selected[item.id]);
    const isIndeterminate = selectedArr.length > 0 && !isAllSelected;

    const multiSelectOptions = useMemo(() => {
        const creatorNames = [
            ...new Set(_items.map(({ creatorName }) => creatorName)),
        ];
        const multiSelectOptions = creatorNames.sort().reduce((acc, val) => {
            let optionName = val;
            if (val === ' ') {
                optionName = '(Blanks)';
            }
            const newOption = {
                name: optionName,
                id: val,
            };
            acc.push(newOption);
            return acc;
        }, []);
        return multiSelectOptions;
    }, [_items]);

    const methodologyMultiSelectOptions = useMemo(
        () => [
            { id: 'team', name: t('tools:collections.methodology.team') },
            { id: 'league', name: t('tools:collections.methodology.league') },
        ],
        [t],
    );

    const hasRhAttributionPermission = client.permissions.includes(
        EDGE_RIGHTSHOLDER_ATTRIBUTION,
    );
    const showMethodologyColumn = useMemo(
        () =>
            hasRhAttributionPermission ||
            _items.some(item => item.methodology === 'league'),
        [hasRhAttributionPermission, _items],
    );

    useEffect(() => {
        getCollections(client.id);
    }, []);

    const _openCreateModal = () => setCreateOpen(true);
    const _closeCreateModal = () => setCreateOpen(false);

    const _openEditModal = id => setEditId(id);
    const isEditMode = !!editId;
    const _closeEditModal = () => setEditId(null);

    const _toggleCollectionSelection = id => {
        const newSelected = { ...selected };
        if (!newSelected[id]) {
            newSelected[id] = true;
        } else {
            newSelected[id] = false;
        }
        setSelected(newSelected);
    };

    const onSelectAllFn = () => {
        if (isAllSelected) {
            setSelected({});
        } else {
            const newSelected = {};
            _items.forEach((item: any) => {
                newSelected[item.id] = true;
            });
            setSelected(newSelected);
        }
    };

    const _archiveCollections = async () => {
        try {
            const selectedIds = getTruthyKeys(selected);
            const selectedCollections = selectedIds
                .map(id => items[id])
                .filter(Boolean);
            const collectionNames = selectedCollections
                .map(collection => collection.name)
                .join(', ');
            const confirmationContent = (
                <Trans
                    i18nKey="tools:collections.alerts.archiveConfirmContent"
                    values={{ names: collectionNames }}
                    components={[<strong key="0" />]}
                />
            );

            Modal.confirm({
                title: t('tools:collections.alerts.archiveConfirmTitle'),
                content: confirmationContent,
                icon: (
                    <i
                        className="fa-solid fa-triangle-exclamation"
                        style={{
                            color: 'rgba(212, 15, 125, 1)',
                            marginRight: '1.1rem',
                            fontSize: '1.2rem',
                            marginTop: '4px',
                        }}
                    />
                ),
                okText: t('tools:collections.alerts.archiveConfirmOk'),
                cancelText: (
                    <span>
                        <i className="fa-regular fa-x -m-r-2" />
                        {t('tools:collections.cancel')}
                    </span>
                ),
                okButtonProps: {
                    style: {
                        marginTop: '1rem',
                        backgroundColor: 'rgba(212, 15, 125, 1)',
                        borderColor: 'rgba(212, 15, 125, 1)',
                    },
                },
                cancelButtonProps: {
                    style: {
                        marginTop: '1rem',
                        backgroundColor: '#000',
                        borderColor: '#000',
                        color: '#fff',
                    },
                },
                onOk: async () => {
                    const collectionIds = getTruthyKeys(selected);
                    const { id: clientId } = client;

                    await archiveCollections(clientId, collectionIds);
                    await getFilters();
                    setSelected({});

                    sendNotifications(
                        t('tools:collections.alerts.archiveSuccess'),
                        {
                            description: (
                                <Trans
                                    i18nKey="tools:collections.alerts.archiveSuccessDesc"
                                    values={{ names: collectionNames }}
                                    components={[<strong key="0" />]}
                                />
                            ),
                        },
                    );
                },
                onCancel: () => {
                    // User cancelled, do nothing
                },
            });
        } catch (e) {
            sendNotifications(t('tools:collections.alerts.archiveError'), {
                context: 'error',
            });
        }
    };

    const archiveCollection = async collectionId => {
        await archiveCollections(client.id, [collectionId]);
    };

    const _getCollectionMessage = () => {
        const insightsTitle = t('measurement:dataExplorer.title');
        const smpTitle = t('measurement:socialMediaExposure.title');
        if (client.permissions.includes(AUTOMATED_COLLECTIONS)) {
            return (
                <p style={{ flex: '1 1 0%', fontSize: 14 }}>
                    <Trans
                        i18nKey="tools:collections.alerts.automatedHelp"
                        values={{ smp: smpTitle }}
                        components={[
                            <Link
                                key="0"
                                className="gds-text--link gds-text--secondary"
                                data-test="help-link"
                                to={`/c/${client.id}/social-media-posts`}
                            />,
                        ]}
                    />
                </p>
            );
        } else {
            return (
                <p style={{ flex: '1 1 0%', fontSize: 14 }}>
                    <Trans
                        i18nKey="tools:collections.alerts.manualHelp"
                        values={{ smp: smpTitle, insights: insightsTitle }}
                        components={[
                            <Link
                                key="0"
                                className="gds-text--link"
                                data-test="help-link"
                                to={`/c/${client.id}/social-media-posts`}
                            />,
                            <Link
                                key="1"
                                className="gds-text--link"
                                data-test="help-link"
                                to={`/c/${client.id}/insights`}
                            />,
                        ]}
                    />
                </p>
            );
        }
    };

    // Set header columns
    const isSelected = itemId => !!selected[itemId];
    const onSelectFn = itemId => () => _toggleCollectionSelection(itemId);
    const onEditFn = itemId => () => _openEditModal(itemId);

    const columns = getCollectionsTableColumns({
        t,
        multiSelectOptions,
        methodologyMultiSelectOptions,
        hasAutomatedCollections,
        isAllSelected,
        isIndeterminate,
        isSelected,
        onSelectFn,
        onEditFn,
        onSelectAllFn,
        showMethodologyColumn,
    });

    const archive = async collectionId => {
        await archiveCollection(collectionId);
    };

    return (
        <ErrorBoundary FallbackComponent={CrashedPage}>
            <Flex vertical style={{ padding: 24 }}>
                <Row gutter={16}>
                    <Col md={24}>
                        <ClientHeader
                            client={client}
                            title={t('tools:collections.title')}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col md={24}>
                        <Alert
                            type="info"
                            showIcon
                            message={_getCollectionMessage()}
                            className="-m-b-3 -p-v-3"
                        />
                    </Col>
                </Row>

                <CollectionsTable
                    hasSelection={hasSelection}
                    _archiveCollections={_archiveCollections}
                    _openCreateModal={_openCreateModal}
                    _items={_items}
                    isLoading={isLoading}
                    columns={columns}
                />

                <AutomatedParamDrawer
                    onClose={_closeCreateModal}
                    title={t('tools:collections.create')}
                    isLoading={false}
                    errorMsg={null}
                    allValues={Object.values(items)}
                    type="collections"
                    create={createCollection}
                    update={updateCollection}
                    archive={archive}
                    open={isCreateOpen}
                />

                <AutomatedParamDrawer
                    onClose={_closeEditModal}
                    title={t('tools:collections.edit')}
                    id={editId}
                    isEditMode={isEditMode}
                    value={items[editId]}
                    isLoading={false}
                    errorMsg={null}
                    type="collections"
                    create={createCollection}
                    update={async values => {
                        await updateCollection(editId, values);
                    }}
                    archive={archive}
                    open={!!editId}
                />
            </Flex>
        </ErrorBoundary>
    );
};

export default connect(['collections', 'client'], actions)(Collections);
